/**
 * 截图批量上传中心（C-6）
 *
 * 路由：/admin/data-import/screenshots · /team/data-import/screenshots · /import/screenshots
 *
 * 6 步向导（无 dry-run，因为不涉及业务字段映射）：
 *   1. 选业务流类型（8 选 1）
 *   2. 选关联导入任务（按 inserted_row_ids 解析 {行号}）
 *   3. 上传 zip 文件（≤ 500MB）
 *   4. 解析预览（matched / unmatched 表格）
 *   5. 执行上传（轮询 progressPercentage）
 *   6. 结果 + 跳客户列表
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Empty, Modal, Progress, Select, Upload, message } from 'antd';
import type { UploadProps } from 'antd';
import { $ } from '../../lib/api';
import { PageHeader, Pill, Btn, StatsRow, StatCard } from '../../components/ui';
import { toast } from '../../lib/toast';
import { fmtDateTimeSec } from '../../lib/format';

const BUSINESS_TYPES = [
  { key: 'customers',  label: '客户分配截图', desc: '客户分配截图（{行号}-{客户名}-{原始名}）' },
  { key: 'follow_ups', label: '跟进截图',     desc: '跟进记录截图' },
  { key: 'contracts',  label: '合同截图',     desc: '合同签约截图' },
  { key: 'invoices',   label: '发票截图',     desc: '发票图片' },
  { key: 'payments',   label: '收款截图',     desc: '收款凭证' },
  { key: 'shipments',  label: '邮寄截图',     desc: '快递面单' },
  { key: 'materials',  label: '资料截图',     desc: '身份证/学历/二寸照' },
  { key: 'orders',     label: '订单截图',     desc: '订单审批截图' },
] as const;
type BusinessType = (typeof BUSINESS_TYPES)[number]['key'];

interface ImportJobSlim {
  id: number;
  importType: string;
  fileName?: string;
  totalRows: number;
  successCount: number;
  status: string;
  createdAt: string;
  insertedRowIds?: number[];
  isSandbox?: number | boolean;
}

interface BatchState {
  id: number;
  businessType: string;
  totalFiles: number;
  matchedCount: number;
  unmatchedCount: number;
  uploadedCount: number;
  failedCount: number;
  status: string;
  progressPercentage: number;
  currentPhase?: string | null;
  previewJson?: { matched?: any[]; unmatched?: any[] } | null;
  resultJson?: { results?: any[]; actuallyInserted?: number } | null;
  notes?: string | null;
}

export function ScreenshotImportPage() {
  const nav = useNavigate();
  const qc = useQueryClient();

  const [step, setStep] = useState(1);
  const [businessType, setBusinessType] = useState<BusinessType | null>(null);
  const [importJobId, setImportJobId] = useState<number | null>(null);
  const [batch, setBatch] = useState<BatchState | null>(null);

  // Step 2: 拉所有 done/partial 状态的导入任务（必须有 insertedRowIds）
  const { data: jobs } = useQuery({
    queryKey: ['imports-list-for-screenshots'],
    queryFn: () => $.get<ImportJobSlim[]>('/imports'),
  });
  const availableJobs = (jobs ?? []).filter(
    (j) => (j.status === 'done' || j.status === 'partial')
      && !j.isSandbox
      && (j.insertedRowIds?.length ?? 0) > 0,
  );

  const upload = useMutation({
    mutationFn: async (file: File) => {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('businessType', businessType!);
      fd.append('importJobId', String(importJobId));
      const { api } = await import('../../lib/api');
      const r = await api.post('/attachments/batches/upload', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 600_000, // zip 大，10 分钟超时
      });
      return r.data as { batchId: number; preview: any };
    },
    onSuccess: async (res) => {
      // 拉完整 batch 状态用于显示
      const full = await $.get<BatchState>(`/attachments/batches/${res.batchId}`);
      setBatch(full);
      setStep(4);
      toast.success(`已上传：${res.preview.totalFiles} 张文件 · 匹配 ${res.preview.matched.length} · 未匹配 ${res.preview.unmatched.length}`);
    },
  });

  const execute = useMutation({
    mutationFn: () => $.post(`/attachments/batches/${batch!.id}/execute`),
    onSuccess: () => {
      setStep(5);
    },
  });

  const restart = () => {
    setStep(1);
    setBusinessType(null);
    setImportJobId(null);
    setBatch(null);
  };

  return (
    <>
      <PageHeader
        breadcrumb={['数据导入', '截图批量上传']}
        title="截图批量上传"
        subtitle="按 {行号}-{客户名}-{原始名} 文件名匹配，批量挂到客户附件"
        actions={<Link to="/admin/data-import"><Btn kind="text">← 返回数据导入中心</Btn></Link>}
      />
      <div className="work-content">
        <StepIndicator step={step} />

        {step === 1 && (
          <Step1BusinessType onSelect={(t) => { setBusinessType(t); setStep(2); }} />
        )}
        {step === 2 && (
          <Step2JobSelect
            jobs={availableJobs}
            businessType={businessType!}
            onSelect={(id) => { setImportJobId(id); setStep(3); }}
            onBack={() => setStep(1)}
          />
        )}
        {step === 3 && (
          <Step3Upload
            onUpload={(f) => upload.mutate(f)}
            uploading={upload.isPending}
            onBack={() => setStep(2)}
          />
        )}
        {step === 4 && batch && (
          <Step4Preview
            batch={batch}
            executing={execute.isPending}
            onExecute={() => execute.mutate()}
            onBack={() => setStep(3)}
          />
        )}
        {step === 5 && batch && (
          <Step5Progress batch={batch} setBatch={setBatch} onRestart={restart} />
        )}
      </div>
    </>
  );
}

/* ─── Step Indicator ─────────────────────────────── */
function StepIndicator({ step }: { step: number }) {
  const labels = ['选业务流', '选导入任务', '上传 zip', '解析预览', '执行结果'];
  return (
    <div className="import-stepper">
      {labels.map((l, i) => {
        const idx = i + 1;
        const cls = idx === step ? 'current' : idx < step ? 'done' : 'pending';
        return (
          <div key={l} className={`import-stepper-item ${cls}`}>
            <div className="import-stepper-dot">{idx}</div>
            <div className="import-stepper-label">{l}</div>
          </div>
        );
      })}
    </div>
  );
}

/* ─── Step 1 ─────────────────────────────── */
function Step1BusinessType({ onSelect }: { onSelect: (t: BusinessType) => void }) {
  return (
    <div className="import-card">
      <div className="import-card-title">第 1 步 · 选择业务流类型</div>
      <div style={{ fontSize: 12, color: 'var(--text-3)', marginBottom: 12 }}>
        截图将归属到对应业务表的 attachments，并按客户聚合
      </div>
      <div className="import-type-grid">
        {BUSINESS_TYPES.map((t) => (
          <div key={t.key} className="import-type-card" onClick={() => onSelect(t.key)}>
            <div style={{ fontWeight: 500, marginBottom: 6 }}>{t.label}</div>
            <div className="time-text" style={{ color: 'var(--text-3)' }}>{t.desc}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ─── Step 2 ─────────────────────────────── */
function Step2JobSelect({ jobs, businessType, onSelect, onBack }: {
  jobs: ImportJobSlim[];
  businessType: BusinessType;
  onSelect: (jobId: number) => void;
  onBack: () => void;
}) {
  const businessLabel = BUSINESS_TYPES.find((t) => t.key === businessType)?.label;
  if (jobs.length === 0) {
    return (
      <div className="import-card">
        <div className="import-card-title">第 2 步 · 选关联导入任务</div>
        <Empty description="没有可关联的导入任务（需要 done/partial 状态、非沙盒、有 inserted_row_ids）" />
        <div style={{ textAlign: 'center', marginTop: 16 }}>
          <Btn kind="text" onClick={onBack}>← 上一步</Btn>
          <Link to="/admin/data-import" style={{ marginLeft: 8 }}>
            <Btn kind="primary">先去做客户导入 →</Btn>
          </Link>
        </div>
      </div>
    );
  }
  return (
    <div className="import-card">
      <div className="import-card-title">
        第 2 步 · 选关联导入任务（{businessLabel}）
      </div>
      <div style={{ fontSize: 12, color: 'var(--text-3)', marginBottom: 12 }}>
        截图文件名前缀 <code>{'{行号}'}</code> 会按选定任务的 <code>inserted_row_ids</code> 解析成客户 ID
      </div>
      <table className="ck-table" style={{ width: '100%' }}>
        <thead>
          <tr>
            <th>任务 ID</th><th>类型</th><th>文件名</th><th>客户数</th><th>提交时间</th><th></th>
          </tr>
        </thead>
        <tbody>
          {jobs.map((j) => (
            <tr key={j.id}>
              <td>#{j.id}</td>
              <td><Pill tone="default">{j.importType}</Pill></td>
              <td><span className="time-text">{j.fileName ?? '-'}</span></td>
              <td>{j.insertedRowIds?.length ?? 0}</td>
              <td><span className="time-text">{fmtDateTimeSec(j.createdAt)}</span></td>
              <td><Btn kind="primary" onClick={() => onSelect(j.id)}>选这个</Btn></td>
            </tr>
          ))}
        </tbody>
      </table>
      <div style={{ marginTop: 16 }}>
        <Btn kind="text" onClick={onBack}>← 上一步</Btn>
      </div>
    </div>
  );
}

/* ─── Step 3 ─────────────────────────────── */
function Step3Upload({ onUpload, uploading, onBack }: {
  onUpload: (f: File) => void;
  uploading: boolean;
  onBack: () => void;
}) {
  const props: UploadProps = {
    accept: '.zip',
    maxCount: 1,
    showUploadList: false,
    beforeUpload: (file) => {
      const sizeMB = file.size / 1024 / 1024;
      if (sizeMB > 500) {
        message.error(`zip 文件 ${sizeMB.toFixed(1)}MB 超过 500MB 上限`);
        return Upload.LIST_IGNORE;
      }
      onUpload(file);
      return false;
    },
  };
  return (
    <div className="import-card">
      <div className="import-card-title">第 3 步 · 上传 zip 文件</div>
      <Upload.Dragger {...props} disabled={uploading} style={{ marginTop: 12 }}>
        <p style={{ fontSize: 36, color: 'var(--primary)' }}>⤴</p>
        <p style={{ fontSize: 14, fontWeight: 500 }}>
          {uploading ? '上传中…（大文件可能耗时数分钟）' : '拖拽 zip 到此处，或点击选择'}
        </p>
        <p style={{ fontSize: 12, color: 'var(--text-3)' }}>
          单 zip ≤ 500MB · 单图 ≤ 20MB · 单客户 ≤ 50 张 · 文件名格式：{'{行号}'}-{'{客户名}'}-{'{原始名}'}.png/jpg/...
        </p>
        <p style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 4 }}>
          支持中文文件名（UTF-8 / GBK 编码自动识别）· <strong>上传超时 10 分钟</strong>（大文件请耐心等待）
        </p>
      </Upload.Dragger>
      <div style={{ marginTop: 16 }}>
        <Btn kind="text" onClick={onBack}>← 上一步</Btn>
      </div>
    </div>
  );
}

/* ─── Step 4 ─────────────────────────────── */
function Step4Preview({ batch, executing, onExecute, onBack }: {
  batch: BatchState;
  executing: boolean;
  onExecute: () => void;
  onBack: () => void;
}) {
  const matched = batch.previewJson?.matched ?? [];
  const unmatched = batch.previewJson?.unmatched ?? [];
  const [showAll, setShowAll] = useState(false);
  const unmatchedShown = showAll ? unmatched : unmatched.slice(0, 10);

  return (
    <div className="import-card">
      <div className="import-card-title">第 4 步 · 解析预览（batch #{batch.id}）</div>

      <StatsRow>
        <StatCard label="总文件数" value={batch.totalFiles} unit="个" />
        <StatCard label="将上传" value={batch.matchedCount} unit="张" valueColor="success" />
        <StatCard label="将跳过" value={batch.unmatchedCount} unit="个" valueColor="danger" />
        <StatCard label="涉及客户" value={new Set(matched.map((m: any) => m.customerId)).size} unit="个" />
      </StatsRow>

      {/* 警告：客户名不一致 */}
      {matched.filter((m: any) => m.nameWarning).length > 0 && (
        <div style={{ marginTop: 12, padding: 10, background: '#fff7e6', borderRadius: 6, fontSize: 12, color: 'var(--text-2)' }}>
          ⚠️ {matched.filter((m: any) => m.nameWarning).length} 张图客户名不一致（仍会上传）
        </div>
      )}

      {/* unmatched 清单 */}
      {unmatched.length > 0 && (
        <div style={{ marginTop: 12 }}>
          <div style={{ fontWeight: 500, marginBottom: 8 }}>
            ❌ 跳过文件（{unmatched.length} 个）
          </div>
          <table className="ck-table" style={{ width: '100%' }}>
            <thead>
              <tr>
                <th>文件名</th>
                <th>原因</th>
              </tr>
            </thead>
            <tbody>
              {unmatchedShown.map((u: any, i: number) => (
                <tr key={i}>
                  <td><span className="time-text" style={{ fontFamily: 'monospace' }}>{u.filename}</span></td>
                  <td>{u.reason}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {unmatched.length > 10 && (
            <div style={{ textAlign: 'center', marginTop: 8 }}>
              <Btn kind="text" onClick={() => setShowAll(!showAll)}>
                {showAll ? '折叠' : `查看全部 ${unmatched.length} 个 ↓`}
              </Btn>
            </div>
          )}
        </div>
      )}

      {matched.length === 0 && (
        <div style={{ marginTop: 12, padding: 12, background: 'var(--danger-bg)', color: 'var(--danger-text)', borderRadius: 6, fontSize: 13 }}>
          ❌ 没有可上传的图片，请检查文件名格式或行号范围
        </div>
      )}

      <div style={{ marginTop: 16, display: 'flex', justifyContent: 'space-between' }}>
        <Btn kind="text" onClick={onBack}>← 重新上传</Btn>
        <Btn kind="primary" onClick={onExecute} disabled={executing || matched.length === 0}>
          {executing ? '提交中…' : `开始上传 ${matched.length} 张图`}
        </Btn>
      </div>
    </div>
  );
}

/* ─── Step 5 ─────────────────────────────── */
function Step5Progress({ batch, setBatch, onRestart }: {
  batch: BatchState;
  setBatch: (b: BatchState) => void;
  onRestart: () => void;
}) {
  const isFinal = ['done', 'partial', 'failed', 'cancelled'].includes(batch.status);
  const isRunning = batch.status === 'running';
  const timerRef = useRef<number | null>(null);
  const toastedRef = useRef(false);

  useEffect(() => {
    if (isFinal) {
      if (timerRef.current) { window.clearInterval(timerRef.current); timerRef.current = null; }
      if (!toastedRef.current) {
        toastedRef.current = true;
        const actual = batch.resultJson?.actuallyInserted;
        const actualSuffix = (actual != null && actual !== batch.uploadedCount)
          ? `（DB 实查 ${actual}）` : '';
        if (batch.status === 'done') toast.success(`上传完成：${batch.uploadedCount} 张成功${actualSuffix}`);
        else if (batch.status === 'partial') toast.error(`部分完成：成功 ${batch.uploadedCount} / 失败 ${batch.failedCount}${actualSuffix} — 请查看失败清单`);
        else if (batch.status === 'failed') toast.error(`上传失败${actualSuffix}${batch.notes ? '：' + batch.notes : ''}`);
      }
      return;
    }
    if (!isRunning) return;
    const tick = async () => {
      if (document.visibilityState !== 'visible') return;
      try {
        const fresh = await $.get<BatchState>(`/attachments/batches/${batch.id}`);
        setBatch(fresh);
      } catch { /* 全局拦截器已 toast */ }
    };
    tick();
    timerRef.current = window.setInterval(tick, 1500);
    return () => {
      if (timerRef.current) { window.clearInterval(timerRef.current); timerRef.current = null; }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [batch.id, batch.status]);

  const failedResults = batch.resultJson?.results?.filter((r: any) => !r.ok) ?? [];

  return (
    <div className="import-card">
      <div className="import-card-title">第 5 步 · {isFinal ? '结果' : '进度'}</div>

      {isRunning && (
        <>
          <Progress percent={batch.progressPercentage} status="active" strokeColor="var(--primary)" />
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8, fontSize: 12, color: 'var(--text-3)' }}>
            <span>{batch.currentPhase || '正在上传…'}</span>
            <span>{batch.uploadedCount + batch.failedCount} / {batch.matchedCount} 张</span>
          </div>
        </>
      )}

      {isFinal && (
        <>
          {(batch.status === 'partial' || batch.status === 'failed') && (
            <div style={{
              padding: 12, marginBottom: 12, borderRadius: 6,
              background: 'var(--danger-bg, #fff1f0)', color: 'var(--danger-text, #cf1322)',
              border: '1px solid var(--danger, #ff4d4f)', fontSize: 13,
            }}>
              ⚠️ 本次上传<strong>未全部入库</strong>：成功 {batch.uploadedCount}{
                batch.resultJson?.actuallyInserted != null && batch.resultJson.actuallyInserted !== batch.uploadedCount
                  ? ` (DB 实查 ${batch.resultJson.actuallyInserted})` : ''
              } / 失败 {batch.failedCount}。请查看下方失败清单后再决定是否重传。
            </div>
          )}
          <StatsRow>
            {/* 终态统计卡 — 总数 / 已上传 / 已跳过 / 失败 */}
            <StatCard label="总数" value={batch.matchedCount + batch.unmatchedCount} unit="张" />
            <StatCard
              label={batch.resultJson?.actuallyInserted != null ? 'DB 实查' : '已上传'}
              value={batch.resultJson?.actuallyInserted ?? batch.uploadedCount}
              unit="张"
              valueColor={batch.status === 'done' ? 'success' : 'warning'}
            />
            <StatCard label="已跳过" value={batch.unmatchedCount} unit="张" />
            <StatCard label="失败" value={batch.failedCount} unit="张" valueColor="danger" />
          </StatsRow>

          {failedResults.length > 0 && (
            <div style={{ marginTop: 12 }}>
              <div style={{ fontWeight: 500, marginBottom: 8 }}>失败清单（{failedResults.length} 张）</div>
              <table className="ck-table" style={{ width: '100%' }}>
                <thead>
                  <tr>
                    <th>文件名</th><th>客户 ID</th><th>失败原因</th>
                  </tr>
                </thead>
                <tbody>
                  {failedResults.slice(0, 20).map((r: any, i: number) => (
                    <tr key={i}>
                      <td><span className="time-text" style={{ fontFamily: 'monospace' }}>{r.filename}</span></td>
                      <td>#{r.customerId}</td>
                      <td>{r.error}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {batch.notes && batch.status === 'failed' && (
            <div style={{ marginTop: 12, padding: 10, background: 'var(--danger-bg)', color: 'var(--danger-text)', borderRadius: 6, fontSize: 12 }}>
              {batch.notes}
            </div>
          )}
        </>
      )}

      <div style={{ marginTop: 24, display: 'flex', gap: 8 }}>
        <Btn kind="primary" onClick={onRestart}>再传一批</Btn>
        <Link to="/customers">
          <Btn kind="text">查看客户列表 →</Btn>
        </Link>
      </div>
    </div>
  );
}
